import { useCallback, useEffect, useRef, useState } from 'react'
import { Eraser, Paintbrush } from 'lucide-react'
import { paintBlurDot } from './ScreenshotCapture'

const DEFAULT_RADIUS = 24

/**
 * Editable preview of a captured screenshot — user can paint additional blur
 * over regions the auto-mask missed.
 *
 * Props:
 *   - dataUrl: initial image
 *   - onChange({ blob, dataUrl })  — emitted on every stroke end
 */
export default function BlurBrush({ dataUrl, onChange }) {
    const canvasRef = useRef(null)
    const wrapRef = useRef(null)
    const [drawing, setDrawing] = useState(false)
    const [brushOn, setBrushOn] = useState(true)
    const [dims, setDims] = useState({ w: 0, h: 0 })

    useEffect(() => {
        if (!dataUrl) return
        const img = new Image()
        img.onload = () => {
            const canvas = canvasRef.current
            if (!canvas) return
            canvas.width = img.naturalWidth
            canvas.height = img.naturalHeight
            const ctx = canvas.getContext('2d')
            ctx.drawImage(img, 0, 0)
            setDims({ w: img.naturalWidth, h: img.naturalHeight })
        }
        img.src = dataUrl
    }, [dataUrl])

    const _emit = useCallback(() => {
        const canvas = canvasRef.current
        if (!canvas || !onChange) return
        canvas.toBlob(
            (blob) => {
                if (!blob) return
                onChange({ blob, dataUrl: canvas.toDataURL('image/png', 0.85) })
            },
            'image/png',
            0.85,
        )
    }, [onChange])

    const _coords = (e) => {
        const canvas = canvasRef.current
        if (!canvas) return null
        const rect = canvas.getBoundingClientRect()
        const scaleX = canvas.width / rect.width
        const scaleY = canvas.height / rect.height
        const point = e.touches?.[0] || e
        return {
            x: (point.clientX - rect.left) * scaleX,
            y: (point.clientY - rect.top) * scaleY,
        }
    }

    const _start = (e) => {
        if (!brushOn) return
        const pt = _coords(e)
        if (!pt) return
        setDrawing(true)
        paintBlurDot(canvasRef.current, pt.x, pt.y, DEFAULT_RADIUS)
    }

    const _move = (e) => {
        if (!drawing) return
        const pt = _coords(e)
        if (!pt) return
        paintBlurDot(canvasRef.current, pt.x, pt.y, DEFAULT_RADIUS)
    }

    const _end = () => {
        if (!drawing) return
        setDrawing(false)
        _emit()
    }

    const _clear = () => {
        if (!dataUrl) return
        const img = new Image()
        img.onload = () => {
            const canvas = canvasRef.current
            if (!canvas) return
            const ctx = canvas.getContext('2d')
            ctx.clearRect(0, 0, canvas.width, canvas.height)
            ctx.drawImage(img, 0, 0)
            _emit()
        }
        img.src = dataUrl
    }

    return (
        <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                    type="button"
                    className={`btn btn--sm ${brushOn ? 'btn--primary' : 'btn--ghost'}`}
                    onClick={() => setBrushOn(!brushOn)}
                    aria-pressed={brushOn}
                    style={{ fontSize: 12 }}
                >
                    <Paintbrush size={14} style={{ marginRight: 4 }} />
                    Кисть {brushOn ? 'вкл' : 'выкл'}
                </button>
                <button type="button" className="btn btn--secondary btn--sm" onClick={_clear}>
                    <Eraser size={14} style={{ marginRight: 4 }} />
                    Сбросить кисть
                </button>
                <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                    {dims.w}×{dims.h}
                </span>
            </div>
            <div
                ref={wrapRef}
                style={{
                    maxHeight: 320,
                    overflow: 'auto',
                    border: '1px solid var(--color-border)',
                    borderRadius: 8,
                    background: 'var(--color-bg-soft)',
                }}
            >
                <canvas
                    ref={canvasRef}
                    style={{
                        display: 'block',
                        width: '100%',
                        height: 'auto',
                        cursor: brushOn ? 'crosshair' : 'default',
                        touchAction: brushOn ? 'none' : 'auto',
                    }}
                    onMouseDown={_start}
                    onMouseMove={_move}
                    onMouseUp={_end}
                    onMouseLeave={_end}
                    onTouchStart={_start}
                    onTouchMove={_move}
                    onTouchEnd={_end}
                />
            </div>
        </div>
    )
}
